const CONSENT_KEY = 'tiiny_cookie_consent'
const CONSENT_VERSION = '1.0'

export const CONSENT_TYPES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
  PREFERENCES: 'preferences'
}

// Send a Consent Mode v2 update to GTM. Equivalent to gtag('consent','update',state).
// A plain dataLayer.push({...}) does NOT update GTM's internal consent state, so tags
// gated by "Additional Consent Checks" (e.g. Microsoft Clarity requiring analytics_storage)
// would never fire. The command form below is the only way that works.
const pushConsentUpdate = (state) => {
  if (typeof window === 'undefined') return
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', state)
    return
  }
  if (window.dataLayer && Array.isArray(window.dataLayer)) {
    function gtag() {
      window.dataLayer.push(arguments)
    }
    gtag('consent', 'update', state)
  }
}

const consentStateFrom = (consent) => ({
  ad_storage: consent.marketing ? 'granted' : 'denied',
  ad_user_data: consent.marketing ? 'granted' : 'denied',
  ad_personalization: consent.marketing ? 'granted' : 'denied',
  analytics_storage: consent.analytics ? 'granted' : 'denied',
  personalization_storage: consent.preferences ? 'granted' : 'denied',
  functionality_storage: consent.preferences ? 'granted' : 'denied',
  security_storage: consent.necessary ? 'granted' : 'denied'
})

export const getStoredConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.error('Error reading cookie consent from localStorage:', error)
  }
  return null
}

export const hasUserMadeChoice = () => {
  const consent = getStoredConsent()
  return consent && consent.version === CONSENT_VERSION
}

export const setConsent = (consentData) => {
  const consentObject = {
    ...consentData,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString()
  }

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentObject))
    pushConsentUpdate(consentStateFrom(consentData))
  } catch (error) {
    console.error('Error storing cookie consent:', error)
  }
}

export const acceptAllCookies = () => {
  setConsent({
    necessary: true,
    analytics: true,
    marketing: true,
    preferences: true
  })
}

export const declineAllCookies = () => {
  setConsent({
    necessary: true, // Necessary cookies are always required
    analytics: false,
    marketing: false,
    preferences: false
  })
}

export const hasConsent = (type) => {
  const consent = getStoredConsent()
  if (!consent) return false
  return consent[type] === true
}

// Initialize GTM consent mode on page load.
// Note: the consent DEFAULT (denied/granted by region) is set inline in public/index.html
// before GTM loads. This function only ever issues consent UPDATES.
export const initializeConsentMode = (isEURegion = true) => {
  if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
    // eslint-disable-next-line no-console
    console.warn('dataLayer not available during consent initialization')
    return
  }

  const consent = getStoredConsent()

  if (consent) {
    pushConsentUpdate(consentStateFrom(consent))
  } else if (!isEURegion) {
    // Non-EU users: auto-grant all consent
    const autoGrantedConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    }

    const consentObject = {
      ...autoGrantedConsent,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      autoGranted: true
    }

    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consentObject))
    } catch (error) {
      console.error('Error storing auto-granted consent:', error)
    }

    pushConsentUpdate(consentStateFrom(autoGrantedConsent))
  }
  // EU users with no stored consent: leave the inline 'denied' default in place.
}
